import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Collapse,
  Container,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { CONFIG } from '~/config';

import { BOOKING_CONTEXT } from '~/literals';

import { RootState } from '~/types';

interface MobileMenuProps {
  isOpen: boolean;
  handleClose: () => void;
}

function MobileMenu({ handleClose, isOpen }: MobileMenuProps) {
  const theme = useTheme();
  const [isTravelHubOpen, setIsTravelHubOpen] = useState(false);
  const bookingContext = useSelector<RootState, string>(({ app }) => app.bookingContext);

  const route = {
    home: '/',
    flightItinerary: '/itinerary',
  };
  const infoRoot = CONFIG.app.helpCenterLink.replace(/\/$/, '');
  const travelHubLinks = [
    { text: 'Flight Routes', to: `${infoRoot}/cheap-flights` },
    { text: 'Cities', to: `${infoRoot}/cheap-flights-from` },
    { text: 'Visa Itinerary Guides', to: `${infoRoot}/visa-itinerary/` },
    { text: 'Travel Blog', to: `${infoRoot}/blog/` },
    { text: 'Ask for Help', to: `${infoRoot}/ask-for-help/` },
  ];

  const isActive = (link: string): boolean => {
    if (bookingContext === BOOKING_CONTEXT.FLIGHT_ITINERARY && link === route.flightItinerary) {
      return true;
    }

    if ((bookingContext === BOOKING_CONTEXT.FLIGHT || !bookingContext) && link === route.home) {
      return true;
    }

    return false;
  };

  const links = [
    { text: 'Search Flights', to: route.home },
    { text: 'Request Itinerary', to: route.flightItinerary },
  ];

  const handleMenuClose = () => {
    setIsTravelHubOpen(false);
    handleClose();
  };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsTravelHubOpen(false);
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleClose, isOpen]);

  const menuItemSx = (active = false) => ({
    color: active ? theme.palette.primary.main : theme.palette.secondary.main,
    fontWeight: 'bold',
    fontSize: 16,
    '&:hover': {
      color: active ? theme.palette.secondary.main : theme.palette.primary.main,
    },
  });

  return (
    <Collapse in={isOpen} timeout={250} unmountOnExit>
      <Box
        aria-label="Mobile navigation"
        component="nav"
        id="mobile-navigation"
        sx={{
          display: { xs: 'block', md: 'none' },
          width: '100%',
          bgcolor: 'common.white',
          borderTop: 1,
          borderColor: 'divider',
          boxShadow: '0 12px 24px rgba(5, 29, 63, 0.16)',
          color: 'secondary.main',
        }}
      >
        <Container maxWidth="xl" sx={{ py: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 1,
              pb: 1,
            }}
          >
            <Typography component="h2" sx={{ fontWeight: 700 }} variant="h6">
              Menu
            </Typography>
            <IconButton
              aria-label="Close navigation menu"
              color="secondary"
              onClick={handleMenuClose}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <List disablePadding>
            {links.map(link => (
              <ListItemButton
                key={link.to}
                component={RouterLink}
                onClick={handleMenuClose}
                selected={isActive(link.to)}
                sx={{ ...menuItemSx(isActive(link.to)), borderRadius: 1, minHeight: 48 }}
                to={link.to}
              >
                <ListItemText primary={link.text} />
              </ListItemButton>
            ))}
            <ListItemButton
              aria-expanded={isTravelHubOpen}
              aria-haspopup="true"
              onClick={() => setIsTravelHubOpen(currentValue => !currentValue)}
              sx={{ ...menuItemSx(isTravelHubOpen), borderRadius: 1, minHeight: 48 }}
            >
              <ListItemText primary="Travel hub" />
              <ListItemIcon sx={{ color: 'inherit', justifyContent: 'flex-end', minWidth: 32 }}>
                {isTravelHubOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItemIcon>
            </ListItemButton>
            <Collapse in={isTravelHubOpen} timeout="auto" unmountOnExit>
              <List disablePadding>
                {travelHubLinks.map(link => (
                  <ListItemButton
                    key={link.to}
                    component="a"
                    href={link.to}
                    onClick={handleMenuClose}
                    sx={{ ...menuItemSx(false), borderRadius: 1, minHeight: 46, pl: 4 }}
                  >
                    <ListItemText primary={link.text} />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
          </List>
        </Container>
      </Box>
    </Collapse>
  );
}

export default MobileMenu;
